import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { addAdultContactRequest } from '@/api/operation';

import { LoadingButton } from '@/components/Form/LoadingButton';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';

import { EMAIL_PATTERN } from '@/constants/patterns';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { TranslatorFunction } from '@/types/i18n';

import { getAPIErrorMessage } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';

const getValidationSchema = (t: TranslatorFunction) => {
  return yup.object().shape({
    email: yup
      .string()
      .required(t('Email is required'))
      .matches(new RegExp(EMAIL_PATTERN), t('Incorrect email format')),
  });
};

export type AdultContactFormType = yup.InferType<
  ReturnType<typeof getValidationSchema>
>;

type AdultContactFormProps = {
  onSubmitted: (data: AdultContactFormType) => void;
};

export const AdultContactForm = ({ onSubmitted }: AdultContactFormProps) => {
  const user = useAppSelector(selectLoggedInUser);
  const { t } = useTranslate();

  const methods = useForm<AdultContactFormType>({
    resolver: yupResolver(
      getValidationSchema(t),
    ) as Resolver<AdultContactFormType>,
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = methods;

  const { mutate, isPending, error } = useMutation({
    mutationFn: addAdultContactRequest,
    onSuccess: () => {
      onSubmitted(methods.getValues());
    },
  });

  const onSubmit = (data: AdultContactFormType) => {
    mutate({
      operationId: user?.operationId || '',
      email: data.email,
    });
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        direction="column"
        sx={{
          width: '100%',
        }}
        gap={4}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography variant="body2" color="text.secondary">
          {t('page.addAdultContact.body')}
        </Typography>
        <TextInput
          type="email"
          inputMode="email"
          name="email"
          control={control}
          error={!!errors.email}
          helperText={errors.email?.message}
          disabled={isPending}
          fullWidth
          label="Email"
        />
        {error && (
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        )}
        <LoadingButton
          {...getDataTestId('add-adult-contact-submit')}
          type="submit"
          disabled={!isValid || isPending}
          isLoading={isPending}
          fullWidth
        >
          {t('common.button.sendEmail')}
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
};
